import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import Button from '../../../../components/common/Button/Button';
import PermissionGate from '../../../../components/common/PermissionGate';
import {
    sanitizeTextInput,
    validateNumberInput,
    getCharacterCount,
    FIELD_LIMITS,
    NUMBER_LIMITS
} from '../../../../utils/inputValidation';

const OpportunityEditModal = ({ isOpen, onClose, opportunity, onSave, isSaving }) => {
    const [formData, setFormData] = useState({
        opportunityName: '',
        value: '',
        expectedCloseDate: '',
        description: '',
        status: 'open',
        statusChangeReason: ''
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const originalStatus = (() => {
        const stage = opportunity?.stageValue || opportunity?.stage || 'open';
        return stage === 'converted' ? 'open' : stage;
    })();
    const isStatusChanged = formData.status !== originalStatus;

    useEffect(() => {
        if (opportunity && isOpen) {
            // Format date for input if it exists
            let formattedDate = '';
            if (opportunity.expectedCloseDate) {
                try {
                    const dateObj = new Date(opportunity.expectedCloseDate);
                    if (!isNaN(dateObj.getTime())) {
                        formattedDate = dateObj.toISOString().split('T')[0];
                    }
                } catch (e) {
                }
            }

            setFormData({
                opportunityName: opportunity.opportunityName || opportunity.name || '',
                value: opportunity.value || opportunity.amount || '',
                expectedCloseDate: formattedDate,
                description: opportunity.description || '',
                status: originalStatus,
                statusChangeReason: ''
            });
            setErrors({});
            setTouched({});
        }
    }, [opportunity, isOpen]);

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        validateField(field, formData[field]);
    };

    const validateField = (field, value) => {
        let error = '';
        if (field === 'opportunityName' && !value?.trim()) {
            error = 'Opportunity name is required';
        } else if (field === 'value') {
            const numValue = parseFloat(value);
            if (!value || isNaN(numValue) || numValue <= 0) {
                error = 'Valid value > 0 is required';
            } else {
                const maxValue = NUMBER_LIMITS.opportunityValue?.max || 999999999999;
                if (numValue > maxValue) {
                    error = `Value cannot exceed ${maxValue.toLocaleString()}`;
                }
            }
        } else if (field === 'expectedCloseDate') {
            if (!value) {
                error = 'Expected close date is required';
            } else {
                const selectedDate = new Date(value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (selectedDate < today) {
                    error = 'Date cannot be in the past';
                }
            }
        } else if (field === 'statusChangeReason') {
            if (isStatusChanged && !value?.trim()) {
                error = 'Status change reason is required';
            } else if (value && value.length > 1000) {
                error = 'Status change reason cannot exceed 1000 characters';
            }
        }

        setErrors(prev => ({ ...prev, [field]: error }));
        return !error;
    };

    const handleInputChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
        let processedValue = value;

        if (fieldType === 'text' || fieldType === 'textarea') {
            processedValue = sanitizeTextInput(String(value || ''));
            processedValue = processedValue.slice(0, maxLength);
        } else if (fieldType === 'number') {
            const maxValue = (field === 'value' ? NUMBER_LIMITS.opportunityValue?.max : NUMBER_LIMITS[field]?.max) || 999999999999;
            const minValue = NUMBER_LIMITS[field]?.min || 0;
            const numValidation = validateNumberInput(value, maxValue, minValue);
            processedValue = numValidation.value;
        }

        setFormData(prev => ({ ...prev, [field]: processedValue }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const validate = () => {
        const fields = ['opportunityName', 'value', 'expectedCloseDate'];
        if (isStatusChanged) {
            fields.push('statusChangeReason');
        }
        let isValid = true;
        const newTouched = {};

        fields.forEach(field => {
            newTouched[field] = true;
            if (!validateField(field, formData[field])) {
                isValid = false;
            }
        });

        setTouched(newTouched);
        return isValid;
    };

    const handleSave = () => {
        if (validate()) {
            const mappedStage = formData.status === 'open' ? 'converted' : formData.status;
            onSave({
                name: formData.opportunityName.trim(),
                amount: parseFloat(formData.value),
                description: formData.description,
                expectedCloseDate: new Date(formData.expectedCloseDate).toISOString(),
                stage: mappedStage,
                statusChangeReason: isStatusChanged ? formData.statusChangeReason : undefined
            });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Edit Opportunity</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Opportunity Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.opportunityName}
                                onChange={(e) => handleInputChange('opportunityName', e.target.value, 'text', FIELD_LIMITS.opportunityName)}
                                onBlur={() => handleBlur('opportunityName')}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${touched.opportunityName && errors.opportunityName ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                                placeholder="Enter opportunity name"
                                maxLength={FIELD_LIMITS.opportunityName}
                            />
                            <div className="flex justify-between mt-1">
                                {touched.opportunityName && errors.opportunityName && (
                                    <p className="text-sm text-red-600 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-1" />
                                        {errors.opportunityName}
                                    </p>
                                )}
                                <p className="text-xs text-gray-500 ml-auto">{getCharacterCount(formData.opportunityName, FIELD_LIMITS.opportunityName)}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Opportunity Value <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    value={formData.value}
                                    onChange={(e) => handleInputChange('value', e.target.value, 'number')}
                                    onBlur={() => handleBlur('value')}
                                    min="0"
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${touched.value && errors.value ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                                    placeholder="Enter opportunity value"
                                />
                                {touched.value && errors.value && (
                                    <p className="mt-1 text-sm text-red-600 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-1" />
                                        {errors.value}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Expected Close Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={formData.expectedCloseDate}
                                    onChange={(e) => {
                                        setFormData(prev => ({ ...prev, expectedCloseDate: e.target.value }));
                                        if (errors.expectedCloseDate) setErrors(prev => ({ ...prev, expectedCloseDate: '' }));
                                    }}
                                    onBlur={() => handleBlur('expectedCloseDate')}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${touched.expectedCloseDate && errors.expectedCloseDate ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                                />
                                {touched.expectedCloseDate && errors.expectedCloseDate && (
                                    <p className="mt-1 text-sm text-red-600 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-1" />
                                        {errors.expectedCloseDate}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status <span className="text-red-500">*</span>
                                {originalStatus === 'closed_won' && (
                                    <span className="ml-2 text-xs text-amber-600 font-normal">(Won - status locked)</span>
                                )}
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => {
                                    const newStatus = e.target.value;
                                    setFormData(prev => ({
                                        ...prev,
                                        status: newStatus,
                                        statusChangeReason: newStatus === originalStatus ? '' : prev.statusChangeReason
                                    }));
                                    if (errors.status) setErrors(prev => ({ ...prev, status: '' }));
                                    if (newStatus === originalStatus && errors.statusChangeReason) {
                                        setErrors(prev => ({ ...prev, statusChangeReason: '' }));
                                    }
                                }}
                                disabled={originalStatus === 'closed_won'}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${
                                    originalStatus === 'closed_won' ? 'border-gray-300 bg-gray-100 cursor-not-allowed' : 'border-gray-300'
                                }`}
                            >
                                <option value="open">Open</option>
                                <option value="closed_won">Won</option>
                                <option value="closed_lost">Lost</option>
                            </select>
                            {originalStatus === 'closed_won' && (
                                <p className="mt-1 text-xs text-amber-600">You can not change status of Won opportunity.</p>
                            )}
                        </div>

                        {isStatusChanged && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status Change Reason <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows={3}
                                    value={formData.statusChangeReason}
                                    onChange={(e) => handleInputChange('statusChangeReason', e.target.value, 'textarea', 1000)}
                                    onBlur={() => handleBlur('statusChangeReason')}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${touched.statusChangeReason && errors.statusChangeReason ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                                    placeholder={`Why are you changing status from ${originalStatus} to ${formData.status}?`}
                                    maxLength={1000}
                                />
                                <div className="flex justify-between mt-1">
                                    {touched.statusChangeReason && errors.statusChangeReason && (
                                        <p className="text-sm text-red-600 flex items-center">
                                            <AlertCircle className="h-4 w-4 mr-1" />
                                            {errors.statusChangeReason}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-500 ml-auto">{getCharacterCount(formData.statusChangeReason, 1000)}</p>
                                </div>
                            </div>
                        )}



                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                rows={3}
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value, 'textarea', FIELD_LIMITS.opportunityDescription)}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.description ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                                placeholder="Enter opportunity description"
                                maxLength={FIELD_LIMITS.opportunityDescription}
                            />
                            <div className="flex justify-between mt-1">
                                {errors.description && <p className="text-sm text-red-600">{errors.description}</p>}
                                <p className="text-xs text-gray-500 ml-auto">{getCharacterCount(formData.description, FIELD_LIMITS.opportunityDescription)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                        <Button
                            variant="outline"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <PermissionGate permission="lead_management" action="update">
                            <Button
                                variant="primary"
                                icon={isSaving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save className="h-4 w-4" />}
                                onClick={handleSave}
                                disabled={isSaving}
                                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                            >
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </PermissionGate>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OpportunityEditModal;
